import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { PaxAmountField, ScopeFields } from "./PricingRuleForm";

const blank = {
  name: "", active: true,
  supplier: "all", airline: "all",
  adt: { type: "flat", value: 0 },
  chd: { type: "flat", value: 0 },
  inf: { type: "flat", value: 0 },
  per_segment: false,
};

const amt = (p) => {
  if (!p || !p.value) return "—";
  return p.type === "percent" ? `${p.value}%` : `$${p.value}`;
};
const fmtCreated = (r) => {
  const dt = r.created_at ? new Date(r.created_at) : null;
  const date = dt && !isNaN(dt) ? dt.toLocaleDateString("en-GB") + " " + dt.toLocaleTimeString("en-GB").slice(0, 5) : "—";
  return { date, by: r.created_by || "—" };
};

export default function AdminConvenienceFees() {
  const [rows, setRows] = useState([]);
  const [editor, setEditor] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/convenience-fees");
      setRows(data || []);
    } catch {
      toast.error("Failed to load convenience fees");
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => setEditor({ mode: "create", rule: { ...blank } });
  const openEdit   = (r) => setEditor({ mode: "edit",   rule: { ...blank, ...r } });

  const save = async () => {
    const r = editor.rule;
    if (!r.name?.trim()) { toast.error("Fee name is required"); return; }
    setBusy(true);
    try {
      if (editor.mode === "create") {
        await api.post("/admin/convenience-fees", r);
        toast.success("Convenience fee created");
      } else {
        await api.patch(`/admin/convenience-fees/${r.id}`, r);
        toast.success("Convenience fee updated");
      }
      setEditor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this convenience fee?")) return;
    await api.delete(`/admin/convenience-fees/${id}`);
    toast.success("Deleted");
    load();
  };

  const toggleActive = async (r) => {
    await api.patch(`/admin/convenience-fees/${r.id}`, { active: !r.active });
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-convenience-fees">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          All matching convenience fees are <strong>summed</strong>. Applied on base fare; shown as a separate line at checkout.
        </p>
        <Button onClick={openCreate} data-testid="conv-fee-create-btn"
                className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">
          <Plus className="w-4 h-4 mr-1" /> New convenience fee
        </Button>
      </div>

      <Card className="overflow-x-auto">
        <Table className="min-w-[1100px]">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Airline</TableHead>
              <TableHead className="text-right">ADT</TableHead>
              <TableHead className="text-right">CHD</TableHead>
              <TableHead className="text-right">INF</TableHead>
              <TableHead>Per-seg</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Created</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const c = fmtCreated(r);
              return (
                <TableRow key={r.id} data-testid={`conv-fee-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-xs uppercase font-mono">{r.supplier || "all"}</TableCell>
                  <TableCell className="text-xs uppercase font-mono">{r.airline || "all"}</TableCell>
                  <TableCell className="text-right font-mono">{amt(r.adt)}</TableCell>
                  <TableCell className="text-right font-mono">{amt(r.chd)}</TableCell>
                  <TableCell className="text-right font-mono">{amt(r.inf)}</TableCell>
                  <TableCell>{r.per_segment ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    <Switch checked={!!r.active} onCheckedChange={() => toggleActive(r)}
                            data-testid={`conv-fee-active-${r.id}`} />
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap">
                    <div className="text-slate-700 font-mono">{c.date}</div>
                    <div className="text-slate-400">by {c.by}</div>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)}
                            data-testid={`conv-fee-edit-${r.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(r.id)}
                            data-testid={`conv-fee-delete-${r.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-slate-400 py-10">
                  No convenience fees configured.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="conv-fee-dialog">
          <DialogHeader>
            <DialogTitle>{editor?.mode === "edit" ? "Edit" : "New"} convenience fee</DialogTitle>
          </DialogHeader>

          {editor && (
            <div className="space-y-5">
              <div>
                <Label>Fee name</Label>
                <Input
                  data-testid="conv-fee-name"
                  value={editor.rule.name}
                  onChange={(e) => setEditor({ ...editor, rule: { ...editor.rule, name: e.target.value } })}
                  className="h-10"
                  placeholder="e.g. Booking convenience fee"
                />
              </div>

              <ScopeFields
                idPrefix="conv-fee"
                rule={editor.rule}
                advanced={false}
                set={(p) => setEditor({ ...editor, rule: { ...editor.rule, ...p } })}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-slate-100">
                <PaxAmountField id="conv-fee-adt" label="Adult (ADT)" pax={editor.rule.adt}
                  onChange={(p) => setEditor({ ...editor, rule: { ...editor.rule, adt: p } })} />
                <PaxAmountField id="conv-fee-chd" label="Child (CHD)" pax={editor.rule.chd}
                  onChange={(p) => setEditor({ ...editor, rule: { ...editor.rule, chd: p } })} />
                <PaxAmountField id="conv-fee-inf" label="Infant (INF)" pax={editor.rule.inf}
                  onChange={(p) => setEditor({ ...editor, rule: { ...editor.rule, inf: p } })} />
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  data-testid="conv-fee-active"
                  checked={!!editor.rule.active}
                  onCheckedChange={(v) => setEditor({ ...editor, rule: { ...editor.rule, active: !!v } })}
                />
                <Label className="cursor-pointer"
                       onClick={() => setEditor({ ...editor, rule: { ...editor.rule, active: !editor.rule.active } })}>
                  Active
                </Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="conv-fee-save"
                    className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">
              {busy ? "Saving…" : "Save fee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
